
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, DatePicker, Select, Col, Row, message, Checkbox, Divider } from 'antd';
import { EditEstatePayload, CreateEstatePayload, EditEstateResponse, CreateEstateResponse } from '../../types';
import Cookies from 'js-cookie';
import moment from 'moment';
import { createEstate, editEstate } from './estateService';
import { US_STATES } from './usstates';
import { EIN_RULE, formatEin } from './ein';

const { Option } = Select;

interface AddEstateModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (estate: EditEstateResponse | CreateEstateResponse) => void;
  initialValues?: any; // Contains estate data for editing
  isEdit?: boolean; // Determines if the modal is in edit mode
}

/**
 * The estate's own details: its name, its address and its tax id.
 *
 * The estate is a legal person in its own right, so its address is its own and
 * not the decedent's, whose address is theirs and stays on their form.
 */
const estateFieldsFrom = (values: any) => ({
  description: values.description,
  estateName: values.estateName,
  state: values.state || '',
  county: values.county || '',
  addressLine1: values.addressLine1 || '',
  addressLine2: values.addressLine2 || '',
  city: values.city || '',
  zipCode: values.zipCode || '',
  ein: formatEin(values.ein),
  courtFileNumber: values.courtFileNumber || '',
  willDate: values.willDate ? values.willDate.format('YYYY-MM-DD') : undefined,
  willWitnesses: values.willWitnesses || '',
  codicil1Date: values.codicil1Date ? values.codicil1Date.format('YYYY-MM-DD') : undefined,
  codicil1Witnesses: values.codicil1Witnesses || '',
  codicil2Date: values.codicil2Date ? values.codicil2Date.format('YYYY-MM-DD') : undefined,
  codicil2Witnesses: values.codicil2Witnesses || '',
  expectedProbateCompletionDate: values.expectedProbateCompletionDate ? values.expectedProbateCompletionDate.format('YYYY-MM-DD') : undefined,
  dateOfLetters: values.dateOfLetters ? values.dateOfLetters.format('YYYY-MM-DD') : undefined,
  typeOfLetters: values.typeOfLetters || '',
  trusteeNames: values.trusteeNames || '',
  pendingActionCourtIndex: values.pendingActionCourtIndex || '',
  inventoryFilingFeeInitiallyPaid: values.inventoryFilingFeeInitiallyPaid,
  notFullyDistributedReason: values.notFullyDistributedReason || '',
  expectedDistributionDate: values.expectedDistributionDate ? values.expectedDistributionDate.format('YYYY-MM-DD') : undefined,
  preliminaryLettersReason: values.preliminaryLettersReason || '',
  probateContestExpected: values.probateContestExpected,
  otherTestamentaryAssets: values.otherTestamentaryAssets || '',
});

/**
 * Creates an estate, or edits the estate itself.
 *
 * Creating captures the estate, its owner, and the decedent where one is
 * already known. Editing covers the estate only: the owner and the decedent
 * are people, and each has its own form reached from the estate page. Rolling
 * them together meant renaming an estate rewrote the owner from whatever the
 * form happened to be showing.
 */
const AddEstateModal: React.FC<AddEstateModalProps> = ({
  open,
  onCancel,
  onSubmit,
  initialValues,
  isEdit = false,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  // Populate form fields when initialValues change (for editing)
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        dob: initialValues.dob ? moment(initialValues.dob) : null,
        isDeceased: initialValues.isDeceased || false,
        willDate: initialValues.willDate ? moment(initialValues.willDate) : null,
        codicil1Date: initialValues.codicil1Date ? moment(initialValues.codicil1Date) : null,
        codicil2Date: initialValues.codicil2Date ? moment(initialValues.codicil2Date) : null,
        expectedProbateCompletionDate: initialValues.expectedProbateCompletionDate ? moment(initialValues.expectedProbateCompletionDate) : null,
        dateOfLetters: initialValues.dateOfLetters ? moment(initialValues.dateOfLetters) : null,
        expectedDistributionDate: initialValues.expectedDistributionDate ? moment(initialValues.expectedDistributionDate) : null,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const token = Cookies.get('accessToken') || Cookies.get('token'); // Adjust based on your token storage
      if (!token) {
        message.error('Unauthorized. Please log in.');
        setLoading(false);
        onCancel();
        return;
      }

      // Prepare payload based on mode
      if (isEdit && initialValues) {
        const payload: EditEstatePayload = estateFieldsFrom(values);

        const updatedEstate: EditEstateResponse = await editEstate(initialValues.uuid, payload, token);
        onSubmit(updatedEstate);
        setLoading(false);
        form.resetFields();
        message.success('Estate updated successfully');
        onCancel();
      } else {
        // Create mode
        const payload: CreateEstatePayload = {
          ...estateFieldsFrom(values),
          dob: values.dob ? values.dob.format('YYYY-MM-DD') : '',
          firstName: values.firstName,
          isDeceased: values.isDeceased || false,
          lastName: values.lastName,
          middleName: values.middleName || '',
          prefix: values.prefix || '',
          sex: values.sex || '',
          suffix: values.suffix || '',
        };

        const createdEstate: CreateEstateResponse = await createEstate(payload, token);
        onSubmit(createdEstate);
        setLoading(false);
        form.resetFields();
        message.success('Estate created successfully');
        onCancel();
      }
    } catch (error: any) {
      console.error(error);
      if (error.message === 'Unauthorized') {
        message.error('Unauthorized. Please log in.');
      } else if (!error.errorFields) {
        message.error(error.message || (isEdit ? 'Failed to update estate.' : 'Failed to create estate.'));
      }
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Estate" : "Add Estate"}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={isEdit ? "Save" : "Create"}
      cancelText="Cancel"
      confirmLoading={loading}
      width={720}
    >
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item
          label="Estate Name"
          name="estateName"
          rules={[{ required: true, message: 'Please enter the estate name!' }]}
        >
          <Input placeholder="Enter estate name" />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
        >
          <Input.TextArea rows={2} placeholder="Enter estate description" />
        </Form.Item>

        <Form.Item
          label="EIN"
          name="ein"
          rules={[EIN_RULE]}
          extra="The estate's own tax id from the IRS. Leave blank until it is issued."
          normalize={formatEin}
        >
          <Input placeholder="12-3456789" maxLength={10} />
        </Form.Item>
        <Form.Item label="Surrogate's Court File Number" name="courtFileNumber">
          <Input placeholder="Enter once assigned by the court" />
        </Form.Item>

        {/* The estate's own address. The decedent's is on the decedent form:
            the two are separate legal persons and their post can go to
            different places. */}
        <Divider orientation="left" plain>
          Estate Address
        </Divider>
        <Form.Item label="Address Line 1" name="addressLine1">
          <Input placeholder="Enter street address" />
        </Form.Item>
        <Form.Item label="Address Line 2" name="addressLine2">
          <Input placeholder="Apartment, suite, etc. (optional)" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="City" name="city">
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="County"
              name="county"
              extra="County the petition is filed in."
            >
              <Input placeholder="Enter county" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Zip Code" name="zipCode">
              <Input placeholder="Enter zip code" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="State"
          name="state"
          extra="Where the estate is administered, which is the law that governs it."
        >
          <Select placeholder="Select state" showSearch optionFilterProp="children" allowClear>
            {US_STATES.map((state) => (
              <Option key={state.code} value={state.code}>
                {state.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Divider orientation="left" plain>Will and probate filing</Divider>
        <Row gutter={16}>
          <Col span={8}><Form.Item label="Will date" name="willDate"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={8}><Form.Item label="First codicil date" name="codicil1Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={8}><Form.Item label="Second codicil date" name="codicil2Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
        </Row>
        <Form.Item label="Will witnesses" name="willWitnesses"><Input.TextArea rows={2} placeholder="Names and addresses" /></Form.Item>
        <Form.Item label="First codicil witnesses" name="codicil1Witnesses"><Input.TextArea rows={2} placeholder="Names and addresses" /></Form.Item>
        <Form.Item label="Second codicil witnesses" name="codicil2Witnesses"><Input.TextArea rows={2} placeholder="Names and addresses" /></Form.Item>
        <Form.Item label="Expected probate completion date" name="expectedProbateCompletionDate"><DatePicker style={{ width: '100%' }} /></Form.Item>
        <Row gutter={16}>
          <Col span={12}><Form.Item label="Date letters issued" name="dateOfLetters"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={12}><Form.Item label="Type of letters" name="typeOfLetters"><Input placeholder="For example, Letters Testamentary" /></Form.Item></Col>
        </Row>
        <Form.Item label="Trustee names" name="trusteeNames"><Input.TextArea rows={2} /></Form.Item>
        <Form.Item label="Pending action court and index number" name="pendingActionCourtIndex"><Input /></Form.Item>
        <Form.Item label="Inventory filing fee initially paid" name="inventoryFilingFeeInitiallyPaid"><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item>
        <Form.Item label="Why the estate is not fully distributed" name="notFullyDistributedReason"><Input.TextArea rows={3} /></Form.Item>
        <Form.Item label="Expected distribution date" name="expectedDistributionDate"><DatePicker style={{ width: '100%' }} /></Form.Item>
        <Form.Item label="Reason preliminary letters are needed" name="preliminaryLettersReason"><Input.TextArea rows={2} /></Form.Item>
        <Form.Item name="probateContestExpected" valuePropName="checked"><Checkbox>Probate contest is expected</Checkbox></Form.Item>
        <Form.Item label="Other testamentary assets or instruments" name="otherTestamentaryAssets"><Input.TextArea rows={2} /></Form.Item>

        {!isEdit && (
          <>
            <Divider orientation="left" plain>
              Owner Information
            </Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Prefix" name="prefix">
                  <Input placeholder="e.g., Mr., Ms." />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter the first name!' }]}
                >
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item label="Middle Name" name="middleName">
                  <Input placeholder="Enter middle name (optional)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter the last name!' }]}
                >
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Suffix" name="suffix">
                  <Input placeholder="e.g., Jr., Sr." />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Date of Birth"
                  name="dob"
                >
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Sex"
                  name="sex"
                >
                  <Select placeholder="Select sex">
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="isDeceased"
              valuePropName="checked"
            >
              <Checkbox>Is Deceased</Checkbox>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddEstateModal;
